import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import type { Product } from '../config/site';
import { useMoonbase } from '../lib/moonbase';
import { Icon } from './Icon';

type ProductCardProps = {
  product: Product;
  featured?: boolean;
};

export function MoonbasePrice({ product, className = '' }: { product: Product; className?: string }) {
  return (
    <span className={className}>
      <span data-moonbase-render={`product.${product.moonbaseId}.price`}>{product.priceFallback}</span>
      <s data-moonbase-if={`product.${product.moonbaseId}.has_discount`} data-moonbase-render={`product.${product.moonbaseId}.original_price`}>
        {product.listPriceFallback}
      </s>
    </span>
  );
}

export function ProductActions({ product, compact = false }: { product: Product; compact?: boolean }) {
  const { configured, buyProduct, downloadProduct, openSetup, viewProduct } = useMoonbase();
  const trialLabel = `Start ${product.commerce.trialDays}-day trial`;

  if (!configured) {
    return (
      <div className={`product-actions${compact ? ' product-actions--compact' : ''}`}>
        <button className="button button--dark" type="button" onClick={openSetup}>Buy Emberline</button>
        <button className="button button--ghost" type="button" onClick={openSetup}>{trialLabel}</button>
      </div>
    );
  }

  return (
    <div className={`product-actions${compact ? ' product-actions--compact' : ''}`}>
      <button
        className="button button--dark"
        type="button"
        hidden
        data-moonbase-if={`!product.${product.moonbaseId}.owned`}
        onClick={() => void buyProduct(product.moonbaseId)}
      >
        Buy Emberline
      </button>
      <button
        className="button button--dark"
        type="button"
        hidden
        data-moonbase-if={`product.${product.moonbaseId}.owned`}
        onClick={() => downloadProduct(product.moonbaseId)}
      >
        <Icon name="download" /> Download Emberline
      </button>
      <button className="button button--ghost" type="button" onClick={() => viewProduct(product.moonbaseId)}>{trialLabel}</button>
    </div>
  );
}

export function ProductCard({ product, featured = false }: ProductCardProps) {
  return (
    <article className={`product-card product-card--emberline${featured ? ' product-card--featured' : ''}`} style={{ '--product-accent': product.accent } as CSSProperties}>
      <Link className="product-card__art" to={`/plugins/${product.slug}`} aria-label={`View ${product.name}`}>
        <img src={product.artwork} alt={`${product.name} plugin interface`} loading="lazy" />
        <span className="product-card__category">{product.category}</span>
      </Link>
      <div className="product-card__body">
        <div className="product-card__heading">
          <div>
            <span className="eyebrow">{product.eyebrow}</span>
            <h3><Link to={`/plugins/${product.slug}`}>{product.name}</Link></h3>
          </div>
          <MoonbasePrice product={product} className="product-card__price" />
        </div>
        <p>{product.shortDescription}</p>
        <div className="product-card__meta">
          <span>{product.formats.join(' · ')}</span>
          <Link to={`/plugins/${product.slug}`}>Details <Icon name="arrow" /></Link>
        </div>
        <ProductActions product={product} compact />
      </div>
    </article>
  );
}
